/* GASSISA
=========================================================================================
* Descripción: Módulo de toma de inventarios - Encabezado
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 5/01/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 06/04/2026 | Lic. José Luis Aragón C.         | Mejora para tipo inventario
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import Autocomplete from "../../ui/Autocomplete";
import { Box, Divider, Typography, Chip } from "@mui/material";
import Grid from "@mui/material/Grid2";

const TIPO_INVENTARIO_ENUM = {
  GENERAL: "GENERAL",
  SELECTIVO: "SELECTIVO",
};

const TIPO_INVENTARIO_OPTIONS = [
  {
    value: TIPO_INVENTARIO_ENUM.GENERAL,
    label: "General - Todos los artículos",
  },
  {
    value: TIPO_INVENTARIO_ENUM.SELECTIVO,
    label: "Selectivo - Artículos específicos",
  },
];

const STATUS_LABEL = {
  PE: "Pendiente",
  AB: "Activo",
  CE: "Cerrado",
  AP: "Aplicado",
};

export default function FrmTomaInventarioHeader({
  value,
  onChange,
  loading,
  listaAlmacenesExhibidores = [],
}) {
  const isNuevo = !value?.folioinventario || value.folioinventario === 0;
  const handle = (k) => (e) => onChange({ ...value, [k]: e.target.value });

  const disabledEdit = !isNuevo;
  const tipoDisabled = !isNuevo;

  const handleAlmacenChange = (event, newValue) => {
    if (typeof newValue === "string") {
      onChange({ ...value, almacenexhibidor: newValue });
    } else if (newValue && typeof newValue === "object") {
      onChange({ ...value, almacenexhibidor: newValue.almacenexhibidor });
    } else {
      onChange({ ...value, almacenexhibidor: "" });
    }
  };

  const tipoValue = (value?.tipoinventario || value?.sTipoInventario || "")
    .trim()
    .toUpperCase();

  const tipoInventarioValue =
    TIPO_INVENTARIO_OPTIONS.find((opt) => opt.value === tipoValue) || null;

  return (
    <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
      {/* Título + Chip */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Typography variant="h6">Toma de Inventario</Typography>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }} sx={{ textAlign: "right" }}>
          {isNuevo && (
            <Chip
              label="Nuevo inventario"
              color="warning"
              variant="outlined"
              size="small"
            />
          )}
        </Grid>
      </Grid>

      {/* Tipo de Inventario */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Autocomplete
            disableClearable
            id="id-tipo-inventario"
            options={TIPO_INVENTARIO_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            value={
              TIPO_INVENTARIO_OPTIONS.find(
                (opt) => opt.value === value?.tipoinventario
              ) ||
              TIPO_INVENTARIO_OPTIONS.find(
                (opt) => opt.value === value?.sTipoInventario
              ) ||
              null
            }
            onChange={(e, newValue) =>
              onChange({
                ...value,
                tipoinventario: newValue?.value || TIPO_INVENTARIO_ENUM.GENERAL,
              })
            }
            disabled={tipoDisabled}
            fullWidth
            noOptionsText="Sin resultados"
            sx={{ minWidth: "250px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tipo inventario"
                fullWidth
                size="small"
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Autocomplete
            freeSolo
            disableClearable
            id="id-almacen-exhibidor-toma"
            options={listaAlmacenesExhibidores}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : option.almacenexhibidor || ""
            }
            value={value?.almacenexhibidor || ""}
            onChange={handleAlmacenChange}
            disabled={disabledEdit}
            fullWidth
            noOptionsText="Sin resultados"
            sx={{ minWidth: "250px" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Almacén / Exhibidor"
                fullWidth
                size="small"
                helperText={
                  isNuevo ? "Seleccione almacén antes de iniciar" : ""
                }
              />
            )}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 1 }} />

      {/* Datos principales */}
      <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField autoComplete="off"
            label="Folio"
            value={value?.folioinventario || 0}
            fullWidth
            disabled
            size="small"
          />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <TextField autoComplete="off"
            label="Responsable"
            value={value?.usuarioresponsable || ""}
            fullWidth
            disabled
            size="small"
          />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <TextField autoComplete="off"
            label="Estatus"
            value={
              STATUS_LABEL[value?.statusinventario] ??
              value?.statusinventario ??
              ""
            }
            fullWidth
            disabled
            size="small"
            color={value?.statusinventario === "AC" ? "success" : "default"}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      {/* Estadísticas */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField autoComplete="off"
            label="Zonas"
            value={value?.totalzonas ?? 0}
            fullWidth
            disabled
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField autoComplete="off"
            label="Artículos"
            value={value?.totalarticulos ?? 0}
            fullWidth
            disabled
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField autoComplete="off"
            label="Inventariados"
            value={value?.totalinventariados ?? 0}
            fullWidth
            disabled
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField autoComplete="off"
            label="Faltantes"
            value={value?.totalfaltantes ?? 0}
            fullWidth
            disabled
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField autoComplete="off"
            label="Con diferencias"
            value={value?.articuloscondiferencias ?? 0}
            fullWidth
            disabled
            size="small"
          />
        </Grid>
      </Grid>
    </Box>
  );
}
